import type { InjectedRouter } from 'react-router';

import { window } from '../../../globals';
import BannerManager from '../../BannerManager';
import { Link } from '../../generic';
import { FormGroup } from '../../generic/Forms';
import FOSSALogoWithCatWhite from '../../generic/FOSSAInlineSVG/FOSSALogoWithCatWhite';
import ForgotPasswordLink from '../common/ForgotPasswordLink';
import SignInSignUpToggle from '../common/SignInSignUpToggle';
import AuthContextProvider, { useAuthContext } from '../context/AuthContext';
import AuthForm from '../form';
import MfaVerification from '../form/MfaVerification';
import CenteredLayout from '../layouts/Centered';
import type { RouterProps } from '../../../types';

/**
 * Page for logging into FOSSA.
 *
 * AuthContext manages the state for logging in, this is just the markup for that page.
 */
const Login = () => {
  const {
    email: {
      ref: emailInputRef,
      ...email
    },
    password: {
      ref: passwordInputRef,
      ...password
    },
    alert,
    status,
    passwordStrength,
    hidePasswordInput,
    isLdapAuthRequired,
    step,
    page,
    submitButton,
    loginMethods,
    inviteToken,
    samlURL,
    hasInvite,
    next: nextUrl,
  } = useAuthContext();
  return (
    <CenteredLayout>
      <div className="fossa-auth__layout__logo">
        <Link to="https://fossa.com/" openInNewTab>
          <FOSSALogoWithCatWhite width={150} />
        </Link>
      </div>
      <AuthForm.Container onSubmit={submitButton.onSubmit}>
        <AuthForm.Heading heading={step === 'verifyMfa' ? 'Two-Factor Authentication' : 'Sign In'} />
        <AuthForm.Alert alert={alert} status={status} />
        {step === 'verifyMfa' ? (
          <MfaVerification />
        ) : (
          <>
            <FormGroup>
              <AuthForm.EmailInput
                ref={emailInputRef}
                email={email}
                hidePasswordInput={hidePasswordInput}
                step={step}
                onSubmit={submitButton.onSubmit}
                hasInvite={hasInvite}
              />
              <AuthForm.PasswordInput
                ref={passwordInputRef}
                step={step}
                password={password}
                hidePasswordInput={hidePasswordInput}
                passwordStrength={passwordStrength}
                onSubmit={submitButton.onSubmit}
              />
              {!isLdapAuthRequired && <ForgotPasswordLink />}
            </FormGroup>
            {samlURL && <AuthForm.SamlLoginButton samlURL={samlURL} />}
          </>
        )}
        <AuthForm.SubmitButton
          page={page}
          step={step}
          status={status}
          submitButton={submitButton}
          hidePasswordInput={hidePasswordInput}
        />
        {step !== 'verifyMfa' && (
          <AuthForm.LoginMethods
            loginMethods={loginMethods}
            hidePasswordInput={hidePasswordInput}
            inviteToken={inviteToken}
            page={page}
          />
        )}
      </AuthForm.Container>
      <SignInSignUpToggle page="login" email={email.value} next={nextUrl} />
      {window.on_prem && <BannerManager />}
    </CenteredLayout>
  );
};

type LoginPageProps = {
  location: RouterProps['location'];
  router: InjectedRouter;
};

/**
 * Login page for Auth 2.0
 */
const LoginPage = ({ location, router }: LoginPageProps) => (
  <AuthContextProvider location={location} router={router}>
    <Login />
  </AuthContextProvider>
);

export default LoginPage;
